/**
 * Audit Log Service
 *
 * Provides centralized audit logging for security-sensitive operations.
 * All actions are logged to the audit_logs table for compliance tracking.
 */

import { supabase } from './supabase';

// ============================================
// TYPES
// ============================================

export type AuditAction =
  | 'LOGIN'
  | 'LOGOUT'
  | 'LOGIN_FAILED'
  | 'PASSWORD_RESET'
  | 'MFA_ENROLLED'
  | 'MFA_VERIFIED'
  | 'SESSION_REFRESH'
  | 'IMPERSONATION_START'
  | 'IMPERSONATION_END'
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'EXPORT'
  | 'IMPORT';

export type ResourceType =
  | 'session'
  | 'user'
  | 'tenant'
  | 'store'
  | 'alert'
  | 'case'
  | 'inventory'
  | 'task'
  | 'report';

export interface AuditLogEntry {
  action: AuditAction;
  resourceType: ResourceType;
  resourceId?: string;
  changes?: Record<string, unknown>;
  requestMeta?: {
    userAgent?: string;
    ipAddress?: string;
    location?: GeolocationCoordinates;
    timestamp?: string;
  };
}

interface RequestMetadata {
  userAgent: string;
  language: string;
  platform: string;
  screenResolution: string;
  timestamp: string;
  timezone: string;
}

// ============================================
// HELPERS
// ============================================

/**
 * Collect browser request metadata for audit logging.
 */
function collectRequestMetadata(): RequestMetadata {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}

// ============================================
// AUDIT LOG SERVICE
// ============================================

export const auditLogService = {
  /**
   * Log an audit event to the database.
   */
  log: async (
    tenantId: string,
    userId: string | null,
    entry: AuditLogEntry
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const requestMeta = {
        ...collectRequestMetadata(),
        ...entry.requestMeta,
      };

      // Include changes in request_meta since 'changes' column may not exist
      const fullMeta = {
        ...requestMeta,
        changes: entry.changes || {},
      };

      // Note: Using type assertion for Json compatibility
      const { error } = await supabase.from('audit_logs').insert({
        tenant_id: tenantId,
        user_id: userId,
        action: entry.action.toLowerCase(),
        resource_type: entry.resourceType,
        resource_id: entry.resourceId || null,
        request_meta: fullMeta,
      });

      if (error) {
        if (import.meta.env.DEV) console.error('AuditLogService: Failed to log audit event:', error);
        return { success: false, error: error.message };
      }

      if (import.meta.env.DEV) console.log(`AuditLogService: Logged ${entry.action} for ${entry.resourceType}`);
      return { success: true };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      if (import.meta.env.DEV) console.error('AuditLogService: Exception during logging:', errorMsg);
      return { success: false, error: errorMsg };
    }
  },

  /**
   * Log a LOGIN event.
   */
  logLogin: async (
    tenantId: string,
    userId: string,
    additionalMeta?: Record<string, unknown>
  ): Promise<void> => {
    await auditLogService.log(tenantId, userId, {
      action: 'LOGIN',
      resourceType: 'session',
      resourceId: userId,
      changes: {
        event: 'user_authenticated',
        ...additionalMeta,
      },
    });
  },

  /**
   * Log a LOGOUT event.
   */
  logLogout: async (tenantId: string, userId: string): Promise<void> => {
    await auditLogService.log(tenantId, userId, {
      action: 'LOGOUT',
      resourceType: 'session',
      resourceId: userId,
      changes: { event: 'user_signed_out' },
    });
  },

  /**
   * Log a failed login attempt.
   */
  logLoginFailed: async (
    email: string,
    reason: string,
    metadata?: { userAgent?: string }
  ): Promise<void> => {
    // Log to console for immediate visibility
    if (import.meta.env.DEV) console.warn(`AuditLogService: Login failed for ${email}: ${reason}`);

    // P0.3: Store in failed_logins table for security monitoring
    // Note: log_failed_login RPC function added in migration 20260123000001
    try {
      const { error } = await supabase.rpc('log_failed_login' as never, {
        p_email: email,
        p_user_agent: metadata?.userAgent || navigator.userAgent,
        p_failure_reason: reason,
      } as never);

      if (error) {
        // Don't throw - failed login logging shouldn't block auth flow
        if (import.meta.env.DEV) console.error('Failed to log failed login attempt:', error.message);
      }
    } catch (err) {
      // Silently fail - security logging shouldn't break the app
      if (import.meta.env.DEV) console.error('Error logging failed login:', err);
    }
  },

  /**
   * Log MFA enrollment.
   */
  logMFAEnrolled: async (
    tenantId: string,
    userId: string,
    factorId: string
  ): Promise<void> => {
    await auditLogService.log(tenantId, userId, {
      action: 'MFA_ENROLLED',
      resourceType: 'user',
      resourceId: userId,
      changes: { factorId },
    });
  },

  /**
   * Log impersonation start (for super admins).
   */
  logImpersonationStart: async (
    tenantId: string,
    adminUserId: string,
    targetUserId: string
  ): Promise<void> => {
    await auditLogService.log(tenantId, adminUserId, {
      action: 'IMPERSONATION_START',
      resourceType: 'user',
      resourceId: targetUserId,
      changes: {
        adminUserId,
        targetUserId,
        event: 'impersonation_started',
      },
    });
  },

  /**
   * Log impersonation end.
   */
  logImpersonationEnd: async (
    tenantId: string,
    adminUserId: string,
    targetUserId: string
  ): Promise<void> => {
    await auditLogService.log(tenantId, adminUserId, {
      action: 'IMPERSONATION_END',
      resourceType: 'user',
      resourceId: targetUserId,
      changes: {
        adminUserId,
        targetUserId,
        event: 'impersonation_ended',
      },
    });
  },

  /**
   * Verify RLS policies are active by checking tenant_id in headers.
   * Used for admin debugging/validation.
   */
  verifyRLSActive: async (expectedTenantId: string): Promise<boolean> => {
    try {
      // Attempt to query with current session - RLS should filter by tenant
      const { data, error } = await supabase
        .from('users')
        .select('tenant_id')
        .limit(1);

      if (error) {
        if (import.meta.env.DEV) console.log('AuditLogService: RLS verification - query error:', error.message);
        return false;
      }

      if (data && data.length > 0) {
        const queriedTenantId = data[0].tenant_id;
        const isActive = queriedTenantId === expectedTenantId;
        if (import.meta.env.DEV) {
          console.log(
            `AuditLogService: RLS verification - tenant_id in response: ${queriedTenantId}, ` +
            `expected: ${expectedTenantId}, RLS active: ${isActive}`
          );
        }
        return isActive;
      }

      if (import.meta.env.DEV) console.log('AuditLogService: RLS verification - no data returned (RLS may be blocking)');
      return true; // Empty result might mean RLS is working correctly
    } catch (err) {
      if (import.meta.env.DEV) console.error('AuditLogService: RLS verification failed:', err);
      return false;
    }
  },
};

export default auditLogService;
